import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { ReferenceImage } from 'types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'

interface Props {
  scheduleId: number
  contentId: number
}

export interface Variables {
  media_file: NonNullable<ReferenceImage['media_file']>
}

interface Data {
  reference_image: ReferenceImage
}

interface Context {
  previousReferenceImage?: ReferenceImage
}

export type Options = Omit<UseMutationOptions<ReferenceImage, Error, Variables, Context>, 'mutationFn'>

export const updateReferenceImage = async (scheduleId: number, contentId: number, variables: Variables) => {
  const response = await sendToApi<Data>(`/schedules/${scheduleId}/contents/${contentId}/reference_image`, {
    method: 'PUT',
    body: variables,
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(buildErrorMessage(response.data.errors) || 'Failed to update reference image')
  }

  return response.data.reference_image
}

export const useUpdateReferenceImage = ({ scheduleId, contentId }: Props, options?: Options) => {
  const queryClient = useQueryClient()

  return useMutation<ReferenceImage, Error, Variables, Context>(
    (variables) => updateReferenceImage(scheduleId, contentId, variables),
    {
      onMutate: async ({ media_file }) => {
        await queryClient.cancelQueries({ queryKey: scheduleKeys.referenceImage(scheduleId, contentId) })

        const previousReferenceImage = queryClient.getQueryData<ReferenceImage>(
          scheduleKeys.referenceImage(scheduleId, contentId),
        )

        if (!previousReferenceImage) return undefined

        queryClient.setQueryData<ReferenceImage>(scheduleKeys.referenceImage(scheduleId, contentId), {
          ...previousReferenceImage,
          status: 'successful',
          media_file,
        })

        return { previousReferenceImage }
      },
      onError: (_err, _variables, context) => {
        if (context?.previousReferenceImage) {
          queryClient.setQueryData<ReferenceImage>(
            scheduleKeys.referenceImage(scheduleId, contentId),
            context.previousReferenceImage,
          )
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(scheduleKeys.referenceImage(scheduleId, contentId))
      },
      ...options,
    },
  )
}
